import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { environment } from '../../environments/environment';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import {
  ImportSpaceFromGoogleResponse,
  ResolveGoogleMapsLinkRequest,
} from '../models/google-maps-link-resolve.model';
import {
  MapsLinkImportError,
  SHORT_MAPS_LINK_HELP,
  UNRESOLVED_MAPS_LINK_HELP,
} from '../shared/location/maps-link-import.error';
import {
  extractLatLngFromUrl,
  extractPlaceIdFromUrl,
  extractPlaceTextQueryFromUrl,
  isShortGoogleMapsLink,
  normalizeGoogleMapsUrl,
} from '../shared/location/google-maps-url.utils';
import { GoogleMapsPlaceClientService } from './google-maps-place-client.service';
import { GoogleMapsLoaderService } from './google-maps-loader.service';

@Injectable({ providedIn: 'root' })
export class GoogleMapsLinkImportService extends FirebaseAwareService {
  private readonly functions = inject(Functions);
  private readonly placeClient = inject(GoogleMapsPlaceClientService);
  private readonly mapsLoader = inject(GoogleMapsLoaderService);

  /** Import from Google Maps URL via Cloud Function (geo + AI description). */
  async importSpaceFromGoogle(rawUrl: string): Promise<ImportSpaceFromGoogleResponse> {
    const mapsUrl = normalizeGoogleMapsUrl(rawUrl);
    if (!mapsUrl) {
      throw new MapsLinkImportError('INVALID', 'Enlace de Google Maps no válido.');
    }

    if (isShortGoogleMapsLink(mapsUrl) && !environment.googleMapsServerLinkResolve) {
      throw new MapsLinkImportError('SHORT_LINK', SHORT_MAPS_LINK_HELP);
    }

    if (environment.googleMapsServerLinkResolve) {
      return this.#callCloudFunction(mapsUrl);
    }

    const client = await this.#tryClientResolve(mapsUrl);
    if (!client) {
      throw new MapsLinkImportError('UNRESOLVED', UNRESOLVED_MAPS_LINK_HELP);
    }

    return {
      ...client,
      aiGeneratedDescription: this.#fallbackDescription(client.place),
    };
  }

  async #tryClientResolve(
    mapsUrl: string,
  ): Promise<Omit<ImportSpaceFromGoogleResponse, 'aiGeneratedDescription'> | null> {
    if (!this.mapsLoader.isConfigured()) return null;

    const placeId = extractPlaceIdFromUrl(mapsUrl);
    if (placeId) {
      try {
        const place = await this.placeClient.resolvePlaceId(placeId);
        return { placeId: place.place_id, resolvedUrl: mapsUrl, place };
      } catch (err) {
        console.warn('[MapsLinkImport] getDetails failed:', err);
      }
    }

    const coords = extractLatLngFromUrl(mapsUrl);
    const textQuery = extractPlaceTextQueryFromUrl(mapsUrl);

    try {
      const place = textQuery && coords
        ? await this.placeClient.resolvePlaceNameNear(textQuery, coords.lat, coords.lng)
        : textQuery
          ? await this.placeClient.resolvePlaceByTextQuery(textQuery)
          : coords
            ? await this.placeClient.resolveLatLng(coords.lat, coords.lng)
            : null;
      if (!place) return null;
      return {
        placeId: place.place_id || '',
        resolvedUrl: mapsUrl,
        place,
      };
    } catch (err) {
      console.warn('[MapsLinkImport] place resolve failed:', err);
      return null;
    }
  }

  async #callCloudFunction(mapsUrl: string): Promise<ImportSpaceFromGoogleResponse> {
    return this.inCtx(async () => {
      const call = httpsCallable<
        ResolveGoogleMapsLinkRequest,
        ImportSpaceFromGoogleResponse
      >(this.functions, 'resolveGoogleMapsLink');

      const { data } = await call({ mapsUrl });
      const description = data.aiGeneratedDescription?.trim()
        || this.#fallbackDescription(data.place);
      return { ...data, aiGeneratedDescription: description };
    });
  }

  #fallbackDescription(place: ImportSpaceFromGoogleResponse['place']): string {
    const name = place.name?.trim() || 'Espacio para eventos';
    const address = place.formatted_address?.trim() || '';
    return (
      `${name} es un espacio para celebraciones y eventos privados` +
      (address ? ` en ${address}` : '') +
      `. Reserva por horas con disponibilidad en tiempo real en TimeFlexRent.`
    );
  }
}
